import React from "react";
import { Form } from "react-final-form";
import { FORM_ERROR } from "final-form";
import createDecorator from "final-form-focus";
import { injectIntl, FormattedMessage } from "react-intl";
import { compose } from "redux";
import { connect } from "react-redux";
import { string as yupString } from "yup";
import { Button } from "@ebsco-ui/ebsco-ui";
import sharedMessages from "../../../../../shared-messages";
import mapApiErrorsToFormErrors from "../../../../utils/map-api-errors-to-field-errors";
import submitErrorFieldsHaveChanged from "../../../../utils/submit-error-fields-have-changed";
import {
  submitVerificationDataRequest,
  submitVerificationDataSuccess,
  submitVerificationDataFailure,
  getUsername
} from "../../../../store/password/reset";
import FormErrorNotification from "../../../form-error-notification";
import { TextInputField } from "../../../shared/fields";
import { useEdgeApiContext } from "../../../edge-api";
import RecoveryControls from "../recovery-controls";
import messages from "./enter-verification-code-form-messages";
import submitMessages from "./submit-error-message-descriptors";

import "./enter-verification-code-form.scss";

const focusOnErrors = createDecorator();

const block = "enter-verification-code-form";

const EnterVerificationCodeForm = ({
  intl: { formatMessage },
  submtVerificationDataRequest,
  submtVerificationDataSuccess,
  submtVerificationDataFailure,
  username
}) => {
  const personalUserApi = useEdgeApiContext();

  const verificationCodeValidationSchema = yupString().required(
    formatMessage(messages.requiredVerificationCode)
  );

  const onSubmit = async data => {
    try {
      const { verificationCode } = data;
      submtVerificationDataRequest();

      await personalUserApi.postVerifyVerificationCode(
        verificationCode,
        username
      );
      submtVerificationDataSuccess(verificationCode);
    } catch (error) {
      submtVerificationDataFailure(error);
      let formErrors = {
        [FORM_ERROR]: {
          headerMessage: formatMessage(sharedMessages.somethingWentWrong),
          descriptionMessage: formatMessage(
            messages.unableToSubmitVerificationCode
          )
        }
      };

      if (error.response) {
        const {
          response: { status, data }
        } = error;
        switch (status) {
          case 400:
            const mappedErrors = mapApiErrorsToFormErrors(data.details, key => {
              return submitMessages[key]
                ? formatMessage(submitMessages[key])
                : undefined;
            });
            if (Object.keys(mappedErrors).length) {
              formErrors = mappedErrors;
            }
            break;
          case 429:
            formErrors = {
              [FORM_ERROR]: {
                headerMessage: formatMessage(sharedMessages.somethingWentWrong),
                descriptionMessage: formatMessage(
                  sharedMessages.exceededAllowedRequestAttempts
                )
              }
            };
            break;
          default:
            return formErrors;
        }
      }
      return formErrors;
    }
  };

  return (
    <div className={block}>
      <Form onSubmit={onSubmit} decorators={[focusOnErrors]}>
        {({
          handleSubmit,
          submitting,
          submitFailed,
          submitErrors,
          submitError,
          dirtyFieldsSinceLastSubmit,
          dirty
        }) => {
          const disableSave =
            submitting ||
            !(submitFailed
              ? submitErrorFieldsHaveChanged(
                  submitErrors,
                  dirtyFieldsSinceLastSubmit
                )
              : dirty);

          return (
            <form onSubmit={handleSubmit}>
              {submitError && <FormErrorNotification {...submitError} />}
              <h2 className="email-message">
                <FormattedMessage
                  {...messages.emailMessage}
                  values={{
                    styledUsername: displayStyledUserName(block),
                    username
                  }}
                />
              </h2>
              <TextInputField
                id="verification-code"
                className="verification-code"
                name="verificationCode"
                autoCapitalize="off"
                label={formatMessage(messages.verificationCode)}
                validationSchema={verificationCodeValidationSchema}
                aria-required="true"
              />
              <RecoveryControls />
              <Button
                className="continue-button"
                type="submit"
                disabled={disableSave}
                styleType="solid"
              >
                <FormattedMessage {...sharedMessages.continue} />
              </Button>
            </form>
          );
        }}
      </Form>
    </div>
  );
};

const displayStyledUserName = baseClassName => msg => (
  <span className={`${baseClassName}__username`}>{msg}</span>
);

const mapStateToProps = state => ({
  username: getUsername(state)
});

const mapDispatchToProps = {
  submtVerificationDataRequest: submitVerificationDataRequest,
  submtVerificationDataSuccess: submitVerificationDataSuccess,
  submtVerificationDataFailure: submitVerificationDataFailure
};

export { EnterVerificationCodeForm };
export default compose(
  injectIntl,
  connect(mapStateToProps, mapDispatchToProps)
)(EnterVerificationCodeForm);
